import { useCallback, useSyncExternalStore } from 'react';

/**
 * ═══════════════════════════════════════════════════════════════════════
 * СВЁРНУТО ИЛИ РАЗВЁРНУТО — ЭТО НАСТРОЙКА ЧЕЛОВЕКА, А НЕ РАЗОВОЕ ДЕЙСТВИЕ.
 *
 * Передача 28.08.2026, раздел 24: «оба состояния свёртки — настройка
 * пользователя, а не разовая: хранить в профиле (или локально)
 * и восстанавливать при заходе».
 *
 * ПОЧЕМУ ЭТО НЕ ПРИДИРКА. Свернул человек показатели ради таблицы —
 * значит он смотрит таблицу. Развернуть их обратно при каждом заходе
 * значит каждый раз отнимать у него сто пятьдесят точек высоты и просить
 * нажать снова. Настройка, которая не переживает перезагрузку, — это
 * не настройка, а неудобство с кнопкой.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * ХРАНИМ МЕСТНО, А НЕ В ПРОФИЛЕ, И ВОТ ПОЧЕМУ.
 *
 * Дизайнер оставил выбор: «в профиле (если нужна синхронизация между
 * устройствами) или локально». Синхронизация здесь не нужна и вредна:
 * плотность экрана — свойство ЭКРАНА, а не человека. На ноутбуке
 * показатели уместны, на большом мониторе — тем более, а на телефоне
 * их и так нет. Тащить выбор с одного устройства на другое значит
 * решать за человека там, где он не просил.
 *
 * Вдобавок это лишний запрос при каждом заходе и лишняя запись
 * при каждом нажатии — ради сведения, которое стоит один бит.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * ЧТЕНИЕ И ЗАПИСЬ — В `try`, И ЭТО НЕ ПЕРЕСТРАХОВКА.
 *
 * `localStorage` бросает исключение целиком, а не возвращает `null`:
 * в приватном окне Safari, при запрете сайту хранить данные, при
 * переполнении квоты. Незащищённое обращение роняет ВЕСЬ экран из-за
 * одного бита о плотности.
 * ═══════════════════════════════════════════════════════════════════════
 */

const ПРИСТАВКА = 'bidra.свёртка.';

/** Прочитать. Нет записи или чтение отказало — «развёрнуто». */
export function прочитатьСвёртку(ключ: string): boolean {
  try {
    return localStorage.getItem(ПРИСТАВКА + ключ) === '1';
  } catch {
    return false;
  }
}

export function записатьСвёртку(ключ: string, свёрнуто: boolean): void {
  try {
    if (свёрнуто) localStorage.setItem(ПРИСТАВКА + ключ, '1');
    else localStorage.removeItem(ПРИСТАВКА + ключ);
  } catch {
    /* Не сохранилось — значит не переживёт перезагрузку. В этом заходе
       свёртка всё равно работает: состояние живёт в React. Ронять экран
       из-за этого нельзя. */
  }
}

/**
 * ═══════════════════════════════════════════════════════════════════════
 * СВЁРТКА С ПАМЯТЬЮ — И ОДНА НА ВСЕХ, КТО СПРОСИЛ ПО ТОМУ ЖЕ КЛЮЧУ.
 *
 * ПЕРВАЯ СБОРКА ЭТОГО КРЮЧКА ДЕРЖАЛА СОСТОЯНИЕ В `useState`, и оно
 * оказалось СВОИМ У КАЖДОГО ВЫЗОВА. Кнопка «свернуть показатели» стоит
 * в ряду периода, а сами плитки рисует `Dashboard` — два разных места,
 * два вызова, два независимых состояния. Нажатие меняло состояние
 * КНОПКИ: значок переворачивался, в хранилище ложилась единица,
 * а плитки оставались на экране. Со стороны это выглядело как «кнопка
 * не работает».
 *
 * Поймала сверка: состояние «показатели свёрнуты» не собралось ни
 * на одной из четырёх ширин — полоса не появлялась.
 *
 * ПОЭТОМУ ЗДЕСЬ ОБЩЕЕ ХРАНИЛИЩЕ И `useSyncExternalStore`. Значение
 * лежит в модуле, подписчики уведомляются при каждой записи, и любое
 * число мест на странице показывает одно и то же. Это ровно тот случай,
 * ради которого `useSyncExternalStore` и существует: источник правды
 * вне React, а читателей несколько.
 * ═══════════════════════════════════════════════════════════════════════
 */
const значения = new Map<string, boolean>();
const подписчики = new Map<string, Set<() => void>>();

function значение(ключ: string): boolean {
  const есть = значения.get(ключ);
  if (есть !== undefined) return есть;
  const из = прочитатьСвёртку(ключ);
  значения.set(ключ, из);
  return из;
}

function подписаться(ключ: string, слушатель: () => void): () => void {
  const набор = подписчики.get(ключ) ?? new Set();
  набор.add(слушатель);
  подписчики.set(ключ, набор);
  return () => набор.delete(слушатель);
}

export function useСвёртка(ключ: string): [boolean, () => void] {
  const свёрнуто = useSyncExternalStore(
    useCallback((слушатель: () => void) => подписаться(ключ, слушатель), [ключ]),
    () => значение(ключ),
    /* НА СЕРВЕРЕ И В РАЗБОРЕ РАЗМЕТКИ — ВСЕГДА «РАЗВЁРНУТО». Хранилища
       там нет, а гадать нельзя: первая отрисовка обязана совпасть
       с тем, что придёт из хранилища на клиенте, иначе React честно
       ругается на расхождение. */
    () => false,
  );

  const переключить = useCallback(() => {
    const стало = !значение(ключ);
    значения.set(ключ, стало);
    записатьСвёртку(ключ, стало);
    for (const с of подписчики.get(ключ) ?? []) с();
  }, [ключ]);

  return [свёрнуто, переключить];
}
